import { useState, useEffect, useCallback, useRef } from 'react';

const INTERACTIVE_TARGET_SELECTOR = [
  'input',
  'textarea',
  'select',
  'option',
  'button',
  'a[href]',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[data-disable-slide-nav="true"]',
].join(', ');

function shouldIgnoreSlideNavKey(eventTarget) {
  if (!(eventTarget instanceof Element)) return false;
  return Boolean(eventTarget.closest(INTERACTIVE_TARGET_SELECTOR));
}

export function useSlideNavigation(totalSlides) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideRefs = useRef([]);
  const observerRef = useRef(null);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(totalSlides - 1, idx));
    setCurrentSlide(clamped);
    slideRefs.current[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [totalSlides]);

  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);
  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (shouldIgnoreSlideNavKey(e.target)) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); prev(); }
      if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      if (e.key === 'End') { e.preventDefault(); goTo(totalSlides - 1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [next, prev, goTo, totalSlides]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const activeEntry = entries.reduce((best, entry) => {
          if (!entry.isIntersecting) return best;
          if (!best || entry.intersectionRatio > best.intersectionRatio) return entry;
          return best;
        }, null);

        if (!activeEntry) return;

        const idx = slideRefs.current.indexOf(activeEntry.target);
        if (idx !== -1) setCurrentSlide(idx);
      },
      {
        threshold: 0,
        rootMargin: '-45% 0px -45% 0px',
      }
    );

    slideRefs.current.forEach((ref) => {
      if (ref) observerRef.current?.observe(ref);
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  const registerSlide = useCallback((idx) => (el) => {
    const observer = observerRef.current;
    const previousRef = slideRefs.current[idx];

    if (previousRef && observer) observer.unobserve(previousRef);

    slideRefs.current[idx] = el;

    if (el && observer) observer.observe(el);
  }, []);

  return { currentSlide, goTo, next, prev, registerSlide, total: totalSlides };
}
